import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Shield, Bug, Users, Globe, Skull, Eye, Terminal, Sparkles, AlertTriangle, ArrowRight, X } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

/**
 * ⌘K / Ctrl+K command palette. Type anything:
 *   CVE-2026-33825            → /app/cve/{id}
 *   lockbit                    → threat-actor detail
 *   exchange / microsoft       → filtered CVE view
 *   page names                 → direct navigation
 * Open / close with ⌘K. Esc to dismiss. Arrow keys navigate, Enter opens.
 */

interface PaletteItem {
  id:       string
  label:    string
  hint?:    string
  icon:     React.ElementType
  action:   () => void
  group:    'nav' | 'cve' | 'actor' | 'search'
}

const CVE_RE = /CVE[-\s]?\d{4}[-\s]?\d{4,7}/i

const NAV_ITEMS: { path: string; label: string; icon: React.ElementType; keywords?: string }[] = [
  { path: '/app/cyber',             label: 'Cyber Security',    icon: Shield },
  { path: '/app/cyber-news',        label: 'Cyber Threat News', icon: AlertTriangle, keywords: 'articles feed' },
  { path: '/app/threat-actors',     label: 'Threat Actors',     icon: Users,         keywords: 'mitre misp apt lockbit' },
  { path: '/app/ransomware',        label: 'Ransomware Tracker', icon: Skull,        keywords: 'leak victims gangs' },
  { path: '/app/exposure',          label: 'External Exposure', icon: Eye,           keywords: 'subdomain shadow it ct log' },
  { path: '/app/super-trends',      label: 'Super Trends',      icon: Sparkles,      keywords: 'entity velocity sparkline' },
  { path: '/app/world',             label: 'World Monitor',     icon: Globe },
  { path: '/app/connectors-manage', label: 'Connectors',        icon: Terminal,      keywords: 'integrations splunk crowdstrike' },
  { path: '/app/connectors-health', label: 'Connector Health',  icon: Terminal },
  { path: '/app/help',              label: 'Help & About',      icon: Search,        keywords: 'gap missing features env' },
]

export default function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [index, setIndex] = useState(0)
  const [actorResults, setActorResults] = useState<any[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const nav = useNavigate()

  // Global hotkey
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen(o => !o)
      } else if (e.key === 'Escape' && open) {
        e.preventDefault()
        setOpen(false)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open])

  useEffect(() => {
    if (open) { setQuery(''); setIndex(0); setTimeout(() => inputRef.current?.focus(), 10) }
  }, [open])

  // Actor fuzzy search (debounced)
  useEffect(() => {
    if (!open || query.length < 2) { setActorResults([]); return }
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/api/cyber/threat-actors', { params: { q: query, limit: 8 } })
        setActorResults(r.data.actors || [])
      } catch {
        setActorResults([])
      }
    }, 150)
    return () => clearTimeout(t)
  }, [query, open])

  const close = useCallback(() => setOpen(false), [])

  const items: PaletteItem[] = useMemo(() => {
    const out: PaletteItem[] = []
    const q = query.trim()
    const ql = q.toLowerCase()

    // 1. CVE direct match
    const cveMatch = q.match(CVE_RE)
    if (cveMatch) {
      const normalized = cveMatch[0].toUpperCase().replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^CVE-/, 'CVE-')
      out.push({
        id:    `cve:${normalized}`,
        label: `Open ${normalized}`,
        hint:  'Jump to CVE detail page',
        icon:  Bug,
        group: 'cve',
        action: () => { close(); nav(`/app/cve/${encodeURIComponent(normalized)}`) },
      })
    }

    // 2. Threat-actor matches (from live catalog)
    for (const a of actorResults.slice(0, 6)) {
      out.push({
        id:    `actor:${a.id || a.name}`,
        label: a.name,
        hint:  `${a.sponsor_country || 'Unknown'} · ${a.category || 'actor'}${a.aliases?.length > 1 ? ` · aka ${a.aliases.slice(1, 3).join(', ')}` : ''}`,
        icon:  Users,
        group: 'actor',
        action: () => { close(); nav(`/app/threat-actors?q=${encodeURIComponent(a.name)}`) },
      })
    }

    // 3. Navigation items (direct label / keyword match)
    if (ql.length > 0) {
      for (const n of NAV_ITEMS) {
        const hay = `${n.label} ${n.keywords || ''}`.toLowerCase()
        if (hay.includes(ql)) {
          out.push({
            id:    `nav:${n.path}`,
            label: n.label,
            hint:  n.path,
            icon:  n.icon,
            group: 'nav',
            action: () => { close(); nav(n.path) },
          })
        }
      }
    }

    // 4. Search fallback — ships you to Super Trends with the query
    if (ql.length > 0) {
      out.push({
        id:    `search:${ql}`,
        label: `Search feeds & trends for "${q}"`,
        icon:  Search,
        group: 'search',
        action: () => { close(); nav(`/app/super-trends`); /* user can search from there */ },
      })
    }

    // If empty query, show navigation shortcuts
    if (q.length === 0) {
      for (const n of NAV_ITEMS.slice(0, 8)) {
        out.push({
          id:    `nav:${n.path}`,
          label: n.label,
          hint:  n.path,
          icon:  n.icon,
          group: 'nav',
          action: () => { close(); nav(n.path) },
        })
      }
    }

    return out
  }, [query, actorResults, close, nav])

  useEffect(() => { setIndex(0) }, [items.length])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown')      { e.preventDefault(); setIndex(i => Math.min(i + 1, items.length - 1)) }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setIndex(i => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter')     { e.preventDefault(); items[index]?.action() }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={close}
    >
      <div
        className="card w-full max-w-2xl mx-4 shadow-2xl overflow-hidden border border-border-light"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <Search size={16} className="text-text-muted" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="CVE-2026-… · actor name · page title · keyword"
            className="flex-1 bg-transparent border-none outline-none text-sm text-text-primary placeholder:text-text-muted"
          />
          <kbd className="text-[10px] px-1.5 py-0.5 rounded border border-border text-text-muted font-mono">esc</kbd>
          <button type="button" onClick={close} className="text-text-muted hover:text-text-primary" title="Close">
            <X size={14} />
          </button>
        </div>

        <div className="max-h-[50vh] overflow-y-auto">
          {items.length === 0 && (
            <div className="px-4 py-8 text-center text-text-muted text-sm">
              No matches. Try a CVE ID, actor name, or page title.
            </div>
          )}
          {items.map((item, i) => {
            const Icon = item.icon
            const active = i === index
            return (
              <button
                key={item.id}
                type="button"
                onMouseEnter={() => setIndex(i)}
                onClick={() => item.action()}
                className={clsx(
                  'w-full flex items-center gap-3 px-4 py-2 text-left transition-colors',
                  active ? 'bg-surface-hover' : 'hover:bg-surface-hover/70',
                )}
              >
                <div className={clsx(
                  'w-6 h-6 rounded flex items-center justify-center flex-shrink-0',
                  item.group === 'cve'   ? 'bg-rose/20    text-rose-light' :
                  item.group === 'actor' ? 'bg-amber/20   text-amber-light' :
                  item.group === 'nav'   ? 'bg-blue/20    text-blue-light' :
                                           'bg-surface-hover text-text-muted',
                )}>
                  <Icon size={12} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-text-primary truncate">{item.label}</div>
                  {item.hint && <div className="text-[10px] text-text-muted truncate">{item.hint}</div>}
                </div>
                <span className="text-[10px] text-text-muted uppercase tracking-wider">{item.group}</span>
                {active && <ArrowRight size={12} className="text-text-muted flex-shrink-0" />}
              </button>
            )
          })}
        </div>

        <div className="px-4 py-2 border-t border-border text-[10px] text-text-muted flex items-center gap-3">
          <span><kbd className="font-mono px-1 rounded border border-border">↑↓</kbd> navigate</span>
          <span><kbd className="font-mono px-1 rounded border border-border">↵</kbd> open</span>
          <span className="ml-auto">Open anywhere with <kbd className="font-mono px-1 rounded border border-border">⌘K</kbd></span>
        </div>
      </div>
    </div>
  )
}
